import { ErrorAlert } from "@components/ErrorAlert";
import {
  useWSSnackbar,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";

import {
  queriesPayStatements,
  queriesPayeeEngagements,
  queriesPayerRows,
  queriesWorkItems,
  queriesWorkLoggingConfigs,
  queriesWorkLogs
} from "@query";
import {
  GetPayeeEngagementAsPayerExpandParameterInner,
  PayStatementStatus,
  WorkLogConvertedResourceType
} from "@wingspanhq/wingspan-api-sdk";
import {
  useModalWorkLogHistory,
  WorkLogHistoryWidget
} from "../../components/WorkLogHistoryWidget";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { AlertWorkLogReviewStatusPayee } from "../../components/AlertWorkLogReviewStatusPayee";
import { CardWorkItems } from "../../components/CardWorkItems";
import { PayrollDetails } from "../../components/PayrollDetails";
import { WorkLogAdditionalDetailsEditable } from "../../components/WorkLogAdditionalDetailsEditable";
import { WorkLogAssignmentInfoPayeeView } from "../../components/WorkLogAssignmentInfoPayeeView";
import { WorkLogStatusStepperPayeeView } from "../../components/WorkLogStatusStepperPayeeView";
import { WorkLogsInPayPeriod } from "../../components/WorkLogsInPayPeriod";
import { useModalDeleteWorkLog } from "../../modals/ModalDeleteWorkLog";
import { useModalRequestEditing } from "../../modals/ModalRequestEditing";
import { useModalSubmitForReview } from "../../modals/ModalSubmitForReview";
import { routeHelperPaystubsDetails } from "../../../Paystubs/paths";
import { selectorPaystubsYear } from "../../../Paystubs/selectors/selectorPaystubsYear";
import { routeHelpersWorkLogsPayee } from "../../paths";
import { selectorWorkLogConvertedResourceLabels } from "../../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogSubmitBlockedReason } from "../../selectors/selectorWorkLogSubmitBlockedReason";
import { activeWorkItemsFilter } from "../../utils/activeWorkItemsFilter";
import {
  canPayeeDelete,
  canPayeeEdit,
  canPayeeRequestEditing,
  canPayeeSubmit,
  hasPayeeRequestedEditing,
  isStatusApproved
} from "../../utils/workLogPermissions";

export const RoutePayeeWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const history = useHistory();
  const goToPaystubDetails = routeHelperPaystubsDetails.useGoTo();

  // Query work log
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // Resolve the client from the work log's engagement.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: {
        engagements_id: {
          eq: workLog?.payeeEngagementId ?? undefined
        }
      }
    },
    {
      enabled: !!workLog?.payeeEngagementId
    }
  );

  const payerRow = queryPayerRow.data?.data?.[0];
  const payerId = payerRow?.id;
  const payerPayeeEngagementId = workLog?.payeeEngagementId ?? undefined;

  // Expand definitions on the shared engagement read for the work-item form.
  const queryPayerPayeeEngagement =
    queriesPayeeEngagements.getPayeeEngagementAsPayer.useQuery(
      {
        payeeEngagementId: payerPayeeEngagementId!,
        expand: new Set([
          GetPayeeEngagementAsPayerExpandParameterInner.WORK_DEFINITIONS
        ])
      },
      { enabled: !!payerPayeeEngagementId }
    );

  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Client name off the search row's server-resolved `profile.resolvedName` —
  // the same string the work-log tables and client list show.
  const payerName = payerRow ? selectorSearchRowName(payerRow) : undefined;

  const workDefinitions = queryPayerPayeeEngagement.data?.workDefinitions ?? [];

  // Query payer's work log config (controls whether attachments are required on submission)
  const queryWorkLogConfigForPayee =
    queriesWorkLoggingConfigs.getWorkLoggingConfigForPayee.useQuery(
      { payerAccountId: payerId as string },
      { enabled: !!payerId }
    );

  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: activeWorkItemsFilter(workLogId)
  });

  // Modals and mutations for payee actions
  const modalSubmitForReview = useModalSubmitForReview();
  const modalDeleteWorkLog = useModalDeleteWorkLog();
  const modalRequestEditing = useModalRequestEditing();
  const modalWorkLogHistory = useModalWorkLogHistory();
  const { openSnackbar } = useWSSnackbar();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId || !payerName) {
      return "Work Log Details";
    }

    return payerPayeeEngagement
      ? `Work Log – ${payerName} • ${payerPayeeEngagement.engagementName}`
      : `Work Log – ${payerName}`;
  }, [payerName, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Derived state for work log status
  const status = workLog?.status;
  const canEdit = canPayeeEdit(status);
  const canSubmit = canPayeeSubmit(status);
  const canDelete = canPayeeDelete(status);
  const canRequestEditing = canPayeeRequestEditing(status);
  const editingRequested = hasPayeeRequestedEditing(workLog as any);
  const isApproved = isStatusApproved(status);

  const attachmentsRequired =
    queryWorkLogConfigForPayee.data?.requireAttachments?.enabled === true;

  // A failed items read also blocks submit — we cannot know the log has work —
  // but it must not be reported as "add a work item", which would be a false
  // statement about the log.
  const submitBlockedReason = queryWorkItems.isError
    ? "We couldn’t load this work log’s items, so it can’t be submitted yet. Refresh to try again."
    : selectorWorkLogSubmitBlockedReason({
        workItems: queryWorkItems.data?.data,
        attachmentsRequired,
        attachmentCount: workLog?.attachmentIds?.length ?? 0
      });

  // Action handlers
  // An approved work log becomes a pay statement (W-2) or an invoice (1099) —
  // `convertedResourceType` decides both the copy and the destination. Before
  // approval it is null, so the engagement's own type is what the copy follows.
  //
  // TODO(v3-blocker, WIN-16077): SDK 4.2.0 dropped `engagementType` from the payer search
  // row, and the engagement template it now lives on belongs to the payer's
  // account, so a contractor cannot read it. Left unset, which falls back to the
  // 1099 wording until approval fills in `convertedResourceType`.
  const engagementType = undefined;

  const convertedResourceId = workLog?.convertedResourceId;
  const isConvertedToPayStatement =
    workLog?.convertedResourceType ===
    WorkLogConvertedResourceType.PAY_STATEMENT;
  const convertedResourceLabels = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog?.convertedResourceType,
    engagementType
  });

  const queryPayStatement = queriesPayStatements.getPayStatement.useQuery(
    { payStatementId: convertedResourceId! },
    {
      enabled: !!convertedResourceId && isConvertedToPayStatement,
      retry: false
    }
  );

  // Paid is terminal: the design gives it its own alert headline.
  const isConvertedResourcePaid =
    queryPayStatement.data?.status === PayStatementStatus.PAID;

  const handleViewInvoice = useCallback(() => {
    if (!convertedResourceId) {
      return;
    }

    if (isConvertedToPayStatement) {
      goToPaystubDetails({
        year: selectorPaystubsYear(queryPayStatement.data?.payDate),
        paystubId: convertedResourceId
      });
      return;
    }

    history.push(`/member/invoices/${convertedResourceId}`);
  }, [
    convertedResourceId,
    isConvertedToPayStatement,
    queryPayStatement.data,
    goToPaystubDetails,
    history
  ]);

  const handleSubmitForReview = useCallback(() => {
    if (workLog) {
      modalSubmitForReview.open({
        workLog,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, modalSubmitForReview, queryWorkLog]);

  const handleDeleteWorkLog = useCallback(() => {
    if (workLog) {
      modalDeleteWorkLog.open({
        workLog,
        onClose: result => {
          if (result?.success) {
            history.push(routeHelpersWorkLogsPayee.path);
          }
        }
      });
    }
  }, [workLog, modalDeleteWorkLog, history]);

  const handleRequestEditing = useCallback(() => {
    if (workLog) {
      modalRequestEditing.open({
        workLog,
        clientName: payerName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, payerName, modalRequestEditing, queryWorkLog]);

  const handleViewHistory = useCallback(() => {
    modalWorkLogHistory.open({ workLogId });
  }, [modalWorkLogHistory, workLogId]);

  // Work logs > {client} > {assignment} > Work log #{n}, per the design. The
  // client and assignment crumbs are context only — neither has a payee-facing
  // detail screen to link to — and each is dropped until its name resolves so
  // the trail never shows a placeholder.
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const breadcrumbs = useMemo(
    () => [
      {
        label: "Work logs",
        onClick: () => history.push(routeHelpersWorkLogsPayee.path)
      },
      ...(payerName ? [{ label: payerName }] : []),
      ...(payerPayeeEngagement?.engagementName
        ? [{ label: payerPayeeEngagement.engagementName }]
        : []),
      {
        label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
      }
    ],
    [history, workLog, payerName, payerPayeeEngagement?.engagementName]
  );

  // Build toolbar actions based on work log status
  const toolbarActions = useMemo(() => {
    const actions: WSPageToolbarAction[] = [];

    // Submit for review - Primary button for Draft/InProgress
    if (canSubmit) {
      actions.push({
        label: "Submit for review",
        buttonKind: "Primary",
        onClick: handleSubmitForReview,
        disabled: !!submitBlockedReason,
        buttonOrder: 1
      });
    }

    // TODO(v3-blocker, PLA-3663): no standalone "Add comment" action. Nothing on
    // v3 comments on the work log itself — `WorkLogsApi` has no comment verb and
    // `UpdateWorkLogRequest` no comment field. A comment on a work *item* is
    // payee-callable and changes no status (`ModalEditItem` already sends one),
    // and its event is scoped to the log, so the in-review case could be built
    // on that; an approved log rejects it, because conversion closes the log.
    // Pending a product call on attaching a log comment to a line item.

    // Request editing — offered only while the work log is in review and no
    // request is outstanding. There is deliberately no "Cancel request for
    // editing" counterpart: see the TODO(v3-blocker) in
    // `AlertWorkLogReviewStatusPayee` — nothing on v3 can unset the comment, so
    // the button could only claim to have worked.
    if (canRequestEditing && !editingRequested) {
      actions.push({
        label: "Request editing",
        buttonKind: "Secondary",
        onClick: handleRequestEditing,
        buttonOrder: 1
      });
    }

    // Menu items (shown in three-dot menu)

    // Opens the pay statement (W-2) or invoice (1099) this work log became. It
    // is the whole point of an approved work log for the contractor, so the
    // design surfaces it as a toolbar button rather than a menu item.
    if (isApproved && convertedResourceId) {
      actions.push({
        label: convertedResourceLabels.view,
        buttonKind: "Secondary",
        icon: "dollar-circle",
        onClick: handleViewInvoice,
        // The paystub URL is keyed on the statement's pay date, so clicking
        // before it resolves would build a link with no year.
        disabled: isConvertedToPayStatement && !queryPayStatement.data,
        buttonOrder: 1
      });
    }

    // Delete work log (destructive)
    if (canDelete) {
      actions.push({
        label: "Delete work log",
        icon: "trash",
        onClick: handleDeleteWorkLog,
        destructive: true
      });
    }

    return actions;
  }, [
    isApproved,
    convertedResourceId,
    convertedResourceLabels.view,
    isConvertedToPayStatement,
    queryPayStatement.data,
    canSubmit,
    canDelete,
    canRequestEditing,
    editingRequested,
    handleViewInvoice,
    handleSubmitForReview,
    handleDeleteWorkLog,
    handleRequestEditing,
    submitBlockedReason
  ]);

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.response?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(routeHelpersWorkLogsPayee.path)
              }
            ]
          }}
          description="The work log you're looking for doesn't exist or has been deleted."
          title="Work log not found"
          type="search"
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.response?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(routeHelpersWorkLogsPayee.path)
                }
              ]
            }}
            description="You don't have permission to view this work log."
            title="Access denied"
            type="error"
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        actions={toolbarActions}
        breadcrumbs={breadcrumbs}
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
      >
        {/* Held back until the work items land: they read as absent while the
            list is in flight, and "add a work item" on a log that has them would
            be wrong for as long as the read takes. */}
        {canSubmit &&
        !queryWorkItems.isInitialLoading &&
        submitBlockedReason ? (
          <WSText.ParagraphSm color="gray500">
            {submitBlockedReason}
          </WSText.ParagraphSm>
        ) : null}
      </WSPageToolbar>

      <AlertWorkLogReviewStatusPayee
        workLog={workLog}
        clientName={payerName}
        engagementType={engagementType}
        isConvertedResourcePaid={isConvertedResourcePaid}
        onViewConvertedResource={
          convertedResourceId ? handleViewInvoice : undefined
        }
        onViewHistory={handleViewHistory}
      />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayeeView
              payStatement={queryPayStatement.data}
              payerRow={payerRow}
              payerPayeeEngagement={payerPayeeEngagement}
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
            />

            <ErrorAlert
              role="alert"
              context="WorkLog"
              error={queryPayerPayeeEngagement.error}
            />
            <CardWorkItems
              currentUserRole="payee"
              noEdit={!canEdit}
              otherPartyName={payerName}
              payerId={payerId!}
              payerPayeeEngagementId={payerPayeeEngagementId!}
              resources={{ payerPayeeEngagement }}
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryPayerPayeeEngagement.isInitialLoading
              }
              workDefinitions={workDefinitions}
              workItems={queryWorkItems.data?.data || []}
              workLogId={workLogId}
              onViewHistory={handleViewHistory}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayeeView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogsInPayPeriod
              currentUserRole="payee"
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <PayrollDetails
              currentUserRole="payee"
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogAdditionalDetailsEditable
              attachmentsRequired={attachmentsRequired}
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogHistoryWidget workLogId={workLogId} />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
